import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Grid, TextField } from "@mui/material";
import { FastField, Form, Formik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import LGAutocomplete from "../../atoms/Common/LGAutocomplete.tsx";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import { getProductList } from "../../api/products";
import { getPurchaseOrderList } from "../../api/purchaseOrders";
import {
  createStockMove,
  getStockMoveById,
  updateStockMove,
} from "../../api/stockmoves/index.ts";
import { getCurrencyList } from "../../api/common/currencies";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import { getUserWarehousesByUserId } from "../../api/userWarehouses";
import { useUser } from "../../context/UserContext.js";


const schema = Yup.object().shape({
  purchaseOrder: Yup.object().nullable(),
  product: Yup.object().nullable().required("El producto es obligatorio"),
  warehouseId: Yup.number().required("El almacén es obligatorio"),
  quantity: Yup.number()
    .required("La cantidad es obligatoria")
    .min(0.0001, "La cantidad debe ser mayor que cero"),
  unitPrice: Yup.number().nullable().min(0, "El precio no puede ser negativo"),
  moveDate: Yup.mixed().nullable(),
  currencyId: Yup.number().nullable(),
});

const makeEmptyValues = (warehouseOptions) => ({
  purchaseOrder: null,
  product: null,
  warehouseId: warehouseOptions[0]?.id ?? "",
  quantity: 0,
  unitPrice: 0,
  moveDate: dayjs(),
  currencyId: "",
});

export default function SyntheticInboundStockMoveForm({
  id,
  onSubmit,
  companyId,
  moveTypeId,
  entityPOTypeId,
  warehouseOptions = [],
}) {
  const { openSnack } = useSnack();
  const [loading, setLoading] = useState(Boolean(id));
  const [selectedPurchaseOrder, setSelectedPurchaseOrder] = useState(null);
  const [initialValues, setInitialValues] = useState(
    makeEmptyValues(warehouseOptions)
  );
  const { userData } = useUser();
  useEffect(() => {
    if (!id) {
      setInitialValues(makeEmptyValues(warehouseOptions));
      setLoading(false);
      return;
    }

    const fetchById = async () => {
      setLoading(true);
      try {
        const row = await getStockMoveById(id);
        setInitialValues({
          purchaseOrder:
            row.sourceId != null
              ? {
                  id: row.sourceId,
                  code: row.purchaseOrderCode ?? "",
                  currencyId: row.currencyId,
                  currency: row.currency,
                }
              : null,
          product: row.product
            ? { ...row.product }
            : row.productId
              ? { id: row.productId, code: "" }
              : null,
          warehouseId: row.warehouseId ?? "",
          quantity: Number(row.quantity) || 0,
          unitPrice: row.unitCost != null ? Number(row.unitCost) : 0,
          moveDate: row.moveDate ? dayjs(row.moveDate) : dayjs(),
          currencyId: row.currencyId ?? row.currency?.id ?? "",
        });
      } catch (error) {
        openSnack(handleError(error), "error");
      } finally {
        setLoading(false);
      }
    };

    fetchById();
  }, [id, warehouseOptions]);

  const warehouseParentOptions = useMemo(
    () => warehouseOptions.map((w) => ({ id: w.id, name: w.name })),
    [warehouseOptions]
  );

  const handleSave = async (values) => {
    try {
      const po = values.purchaseOrder;
      const currencyId =
        values.currencyId !== "" && values.currencyId != null
          ? Number(values.currencyId)
          : po?.currencyId ?? po?.currency?.id ?? null;
      const payload = {
        productId: values.product?.id,
        warehouseId: Number(values.warehouseId),
        moveDate: values.moveDate ? dayjs(values.moveDate).toISOString() : null,
        quantity: Number(values.quantity) || 0,
        unitCost:
          values.unitPrice !== "" && values.unitPrice != null
            ? Number(values.unitPrice)
            : null,
        currencyId,
        sourceTypeId: entityPOTypeId ?? -1,
        sourceId: po?.id ?? -1,


      };

      if (!id) {
        await createStockMove({
          ...payload,
          companyId,
          direction: 1,
          moveTypeId,
          sourceTypeId: po?.id ? entityPOTypeId : undefined,
          sourceId: po?.id ?? undefined,
        });
        openSnack("Entrada sintética creada correctamente", "success");
      } else {
        await updateStockMove(id, {
          ...payload,
          sourceTypeId: po?.id ? entityPOTypeId : undefined,
          sourceId: po?.id ?? null,
        });
        openSnack("Entrada sintética actualizada correctamente", "success");
      }

      onSubmit?.();
    } catch (error) {
      openSnack(handleError(error), "error");
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={handleSave}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue }) => (
        <Form>
          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid item xs={12} md={6}>
              <LGAutocomplete
                fetchFunction={getPurchaseOrderList}
                queryParams={{ companyId, pageSize: 20 }}
                optionLabel="code"
                value={values.purchaseOrder}
                onChange={(_, value) => {
                  setFieldValue("purchaseOrder", value);
                  const cid = value?.currencyId ?? value?.currency?.id;
                  if (cid != null) setFieldValue("currencyId", cid);
                }}
                label="Orden de compra"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <LGAutocomplete
                fetchFunction={getProductList}
                queryParams={{  companyId, pageSize: 20 }}
                optionLabel="code"
                value={values.product}
                onChange={(_, value) => setFieldValue("product", value)}
                label="Producto"
                error={Boolean(touched.product && errors.product)}
                helperText={touched.product ? errors.product : ""}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FastField
                name="warehouseId"
                as={DropdownSelector}
                label="Almacén"
                fetchParams={userData.id}
                value={values.warehouseId}
                fetchFunction={getUserWarehousesByUserId}
                optionLabel="warehouse.name"
                keyField="id"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <MyDatePicker
                label="Fecha movimiento"
                value={values.moveDate}
                onChange={(value) => setFieldValue("moveDate", value)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Cantidad"
                type="number"
                value={values.quantity}
                onChange={(event) =>
                  setFieldValue("quantity", event.target.value === "" ? "" : Number(event.target.value))
                }
                error={Boolean(touched.quantity && errors.quantity)}
                helperText={touched.quantity ? errors.quantity : ""}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Precio unitario"
                type="number"
                value={values.unitPrice}
                onChange={(event) =>
                  setFieldValue("unitPrice", event.target.value === "" ? "" : Number(event.target.value))
                }
                error={Boolean(touched.unitPrice && errors.unitPrice)}
                helperText={touched.unitPrice ? errors.unitPrice : ""}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FastField
                name="currencyId"
                as={DropdownSelector}
                label="Moneda"
                fetchFunction={getCurrencyList}
                optionLabel="symbol"
                keyField="id"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button type="submit" variant="contained">
                  Guardar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
}
